import { Link, usePage } from "@inertiajs/react"
import { ChevronRight, Coins } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { formatXpKSuffix, formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import { creatorRewardsPath } from "@/routes"
import type { CurrentBrand } from "@/types"

// OKS-60 — Narrow shared-props slice this pill consumes. Inlining the
// shape (vs. `Partial<SharedProps>`) keeps eslint's `react/prop-types`
// rule happy — a deep-Partial of the canonical SharedProps trips the
// rule since every member is `unknown`-ish; an explicit shape lets it
// resolve. Mirrors the OKS-48 `XpPillSharedSlice` pattern.
interface CoinsPillSharedSlice {
  current_brand_coin_balance?: number
  currentBrand: CurrentBrand | null
  // Index signature satisfies the `usePage<T extends PageProps>`
  // constraint — Inertia ships `Page<T>` whose `props` is widened
  // beyond the named keys we actually consume.
  [key: string]: unknown
}

/**
 * OKS-60 — Creator-shell header pill (left of `<XpPill>`).
 *
 * Lives in `creator-shell.tsx` next to the XP pill. Shows the creator's
 * per-brand coin balance for `Current.brand` (BE shared prop
 * `current_brand_coin_balance` — lambda form, partial-reload friendly).
 *
 * # States (UX Spec §2)
 *
 *   - Empty (`0`)            → render the literal "0", same caption
 *                              colour as a non-zero balance (UX Spec
 *                              §8 a11y — colour is not the sole signal).
 *   - Loading                → `<Skeleton>` sized to the eventual
 *                              width. Triggered when the shared prop is
 *                              `undefined` (creator without a current
 *                              membership) or during a partial reload
 *                              that strips the prop.
 *   - Error                  → pill hides cleanly (no broken UI) per
 *                              UX Spec §3 — implemented via a `null`
 *                              return when the shared prop is missing
 *                              AND we know we're on a creator shell.
 *   - Success                → `1,247` with locale-aware grouping.
 *   - Edge (mobile <320px)   → `1.2K` collapsed via `formatXpKSuffix`
 *                              (the K-suffix helper is XP-named but
 *                              pure numeric — reuse, don't fork).
 *
 * # Accessibility
 *
 *   - `<Link>` from Inertia → focusable, semantic, keyboard-navigable
 *   - `aria-label` reads the FULL number even on the K-collapse variant
 *     so screen readers never get the lossy "1.2K" representation
 *   - Tap target ≥44×44 px via padding on the wrapper
 *   - `aria-busy="true"` on the Skeleton parent during loading
 *
 * # Visual token
 *
 * Amber accent (UX-OQ-2) — distinguishes coins from XP at a glance
 * even when both pills sit side-by-side. The `Coins` lucide icon is
 * decorative (`aria-hidden`); the value alone carries the semantic
 * for screen readers.
 *
 * # Affordance (OKS-600)
 *
 * The pill already navigated to Rewards but read as a passive stat.
 * We layer button-like feedback WITHOUT changing the semantics:
 *
 *   - The `<Link>` is a `group` + `active:scale-[0.98]` (pressed cue).
 *   - The `<Badge>` deepens its amber fill + gains a border/shadow lift
 *     on `group-hover`, and darkens further on `group-active`
 *     (pressed). All transitions ≤150ms (UX Spec §4).
 *   - A decorative `<ChevronRight>` (`aria-hidden`) is the always-on
 *     destination cue — shape, not colour, per UX Spec §6 a11y. It
 *     nudges 2px on hover as a subtle directional hint.
 *
 * The a11y contract is untouched: same Inertia `<Link>`, same
 * `coins.aria_label` (already names "Open rewards in {brand}"), same
 * focus-visible ring, same ≥44px target. No new i18n.
 */
export function CoinsPill() {
  const { t, i18n } = useTranslation()
  // Rename `props` to `pageProps` so react/prop-types doesn't conflate
  // shared-prop access with component prop validation (same OKS-48
  // XpPill workaround).
  const pageProps = usePage<CoinsPillSharedSlice>().props
  const balance = pageProps.current_brand_coin_balance
  const brandName = pageProps.currentBrand?.name ?? ""

  // Loading branch — the shared prop is `undefined` while a partial
  // reload that selects only this prop is in flight, OR before
  // `Current.membership` is resolved on a fresh creator session.
  if (balance === undefined) {
    return (
      <div
        aria-busy="true"
        aria-label={t("coins.loading")}
        className="flex h-11 w-16 min-w-11 items-center justify-center"
        data-testid="coins-pill-loading"
      >
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    )
  }

  const fullNumber = formatXpNumber(balance, i18n.language)
  const collapsedNumber = formatXpKSuffix(balance, i18n.language)
  const ariaLabel = t("coins.aria_label", {
    count: fullNumber,
    brand: brandName,
  })

  return (
    <Link
      href={creatorRewardsPath()}
      aria-label={ariaLabel}
      data-testid="coins-pill"
      className={cn(
        // Tap target padding — outer anchor pads to ≥44×44 around the
        // smaller visual badge (UX Spec §7).
        "group inline-flex h-11 min-w-11 items-center justify-center px-2",
        "focus-visible:ring-ring rounded-md focus-visible:ring-2 focus-visible:outline-none",
        // Pressed cue (OKS-600) — a subtle scale-down on active gives
        // tactile feedback on tap / Space-Enter. ≤150ms (UX Spec §4).
        "transition-transform duration-150 active:scale-[0.98]",
      )}
    >
      <Badge
        variant="secondary"
        data-testid="coins-pill-badge"
        // Amber token (UX-OQ-2) — distinct from XpPill's existing
        // secondary slate to keep both pills visually distinguishable
        // when rendered side-by-side. Dark-mode mirror via the
        // `dark:` prefix.
        //
        // OKS-600 affordance — deepen fill + border + shadow lift on
        // hover, darker fill on pressed. Driven by the parent `group`
        // so the whole chip reacts as one control. Text stays
        // amber-900 (light) / amber-100 (dark) across all states →
        // AA contrast holds against the amber-100→300 (light) and
        // amber-900/40→80 (dark) fills.
        className={cn(
          "border-amber-200 bg-amber-100/80 text-amber-900",
          "dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-100",
          "transition-[background-color,border-color,box-shadow] duration-150",
          "group-hover:border-amber-300 group-hover:bg-amber-200 group-hover:shadow-sm",
          "group-active:bg-amber-300",
          "dark:group-hover:border-amber-700 dark:group-hover:bg-amber-900/60",
          "dark:group-active:bg-amber-900/80",
        )}
      >
        <Coins aria-hidden="true" className="mr-1 size-3.5" />
        {/* Collapsed variant — visible on tight viewports.
            `sr-only` full-number sibling keeps the SR announcement
            faithful to the exact value (UX Spec §8 a11y). */}
        <span aria-hidden="true" className="sm:hidden">
          {collapsedNumber}
        </span>
        <span aria-hidden="true" className="hidden sm:inline">
          {fullNumber}
        </span>
        <span className="sr-only">{fullNumber}</span>
        {/* Destination cue (OKS-600) — decorative chevron signalling
            the pill opens Rewards. Shape carries the affordance (not
            colour) per UX Spec §6; `aria-hidden` keeps the accessible
            name sourced solely from `coins.aria_label`. 2px hover
            nudge is a subtle directional hint (UX Spec §4). */}
        <ChevronRight
          aria-hidden="true"
          data-testid="coins-pill-chevron"
          className="ml-0.5 size-3.5 transition-transform duration-150 group-hover:translate-x-0.5"
        />
      </Badge>
    </Link>
  )
}
